import type { LoginNextStepV1 } from "@strust/contracts";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import {
  useBeginLoginTwoFactorSetup,
  useConfirmLoginTwoFactorSetup,
  useLoginCode,
  useLoginNewPassword,
} from "../api/session.ts";
import { LoginStepScreen } from "../components/login-step-screen.tsx";
import { describeError, errorCode, isCommonPasswordRefusal } from "../model/error-message.ts";

export interface LoginStepProps {
  readonly step: LoginNextStepV1;
  /** A two-factor recovery invitation (the new password is already set). */
  readonly recovery?: boolean;
  /** The session started: leave the public screens. */
  readonly onSignedIn: () => void;
  /** The step expired, or the person went back: show the password form again. */
  readonly onRestart: (reason: "expired" | "back") => void;
  /**
   * A step led to another one (the new password of a first login, then the mandatory set-up):
   * show that step. Without it, the person restarts from the password form.
   */
  readonly onNextStep?: (step: LoginNextStepV1) => void;
}

/**
 * Drives the step after a right password (design D5): the code of the authenticator, the new
 * password replacing a temporary one (change adopt-installation-per-customer; it may lead to the
 * set-up), or the mandatory set-up of an authenticator (begun at once; the secret lives only in the
 * mutation and leaves the screen when the step ends). `mfa_challenge_expired` sends the person
 * back to the password.
 */
export function LoginStep({
  step,
  recovery = false,
  onSignedIn,
  onRestart,
  onNextStep,
}: LoginStepProps) {
  const intl = useIntl();
  const code = useLoginCode();
  const newPassword = useLoginNewPassword();
  const begin = useBeginLoginTwoFactorSetup();
  const confirm = useConfirmLoginTwoFactorSetup();

  const startSetup = begin.mutate;
  useEffect(() => {
    if (step === "mfa_setup_required") startSetup();
  }, [step, startSetup]);

  const expired = [code.error, newPassword.error, begin.error, confirm.error].some(
    (error) => errorCode(error) === "mfa_challenge_expired",
  );
  useEffect(() => {
    if (expired) onRestart("expired");
  }, [expired, onRestart]);

  if (step === "mfa_required")
    return (
      <LoginStepScreen
        step="mfa_required"
        pending={code.isPending}
        error={code.error ? describeError(intl, code.error) : undefined}
        onSubmitCode={(value) => {
          code.mutate(value, { onSuccess: onSignedIn });
        }}
        onRestart={() => {
          onRestart("back");
        }}
      />
    );

  if (step === "new_password_required")
    return (
      <LoginStepScreen
        step="new_password_required"
        pending={newPassword.isPending}
        error={newPassword.error ? describeError(intl, newPassword.error) : undefined}
        commonRefused={isCommonPasswordRefusal(newPassword.error)}
        onSubmitPassword={(value) => {
          newPassword.mutate(value, {
            onSuccess: (outcome) => {
              if (outcome.kind === "session") onSignedIn();
              else if (onNextStep !== undefined) onNextStep(outcome.step);
              else onRestart("back");
            },
          });
        }}
        onRestart={() => {
          onRestart("back");
        }}
      />
    );

  const failure = confirm.error ?? begin.error;
  return (
    <LoginStepScreen
      step="mfa_setup_required"
      recovery={recovery}
      setup={
        begin.data === undefined
          ? undefined
          : { otpauthUri: begin.data.otpauth_uri, secret: begin.data.secret }
      }
      pending={confirm.isPending || begin.isPending}
      error={failure ? describeError(intl, failure) : undefined}
      onConfirm={(value) => {
        confirm.mutate(value, {
          onSuccess: () => {
            begin.reset();
            onSignedIn();
          },
        });
      }}
      onRestart={() => {
        begin.reset();
        onRestart("back");
      }}
    />
  );
}
